/**
 * CallRail REST Helpers (Story 22.1, AC #4, AC #5, AC #7, AC #12)
 *
 * Per-endpoint typed wrappers around `safeFetchCallRail`. Each helper:
 *  - Builds the CallRail v3 URL from typed params.
 *  - Validates required params (throws `TypeError` synchronously on missing).
 *  - Calls `safeFetchCallRail` for token-injected, sanitized fetch.
 *  - Parses the response body via `expectedRecordsPath` shape-check.
 *  - Throws `CallRailApiCallError` on HTTP error mapping per AC #7.
 *
 * **Web research divergence from story AC text (verified
 * `apidocs.callrail.com/` 2026-05-14):**
 *
 *  1. **Company filtering uses `?company_id=` query parameter** — NOT
 *     `/c/{company_id}/` URL path segments. The story's AC #4 / AC #12
 *     specify a path-scoped shape; the real API has no such path. We
 *     implement against the actual API and document this in the
 *     story's Completion Notes per the user's Option-1 decision.
 *
 *  2. **`granularity` accepts `hour | day | week | month | year`** — the
 *     story AC #4 lists only `day | week | month`. We accept the full
 *     enum per `fields.ts` `CALLRAIL_GRANULARITY_VALUES`.
 *
 *  3. **Timeseries is capped at 200 data points per response** — the
 *     `fetchCallRailCallsTimeseries` helper surfaces a warning when a
 *     granularity/date-range combination would exceed the cap. The
 *     caller can chunk the range or use coarser granularity.
 *
 *  4. **Default `/calls.json` fields include PII + recording URL** — the
 *     story's Round 3 Red Team #1 premise is correct but understates the
 *     impact. `sanitizeCallRailPii` runs on EVERY record (log boundary),
 *     `stripRecordingUrls` runs at the transformer (persistence boundary).
 *
 *  5. **No documented `Retry-After` header on 429** — fallback to a
 *     60-sec cooldown applied inside `safeFetchCallRail`.
 *
 *  6. **No structured error response body schema** — `parseCallRailApiError`
 *     defensively probes `message`, `error`, `errors`, `detail` JSON
 *     fields and tolerates absence.
 */

import {
  CallRailApiCallError,
  parseCallRailApiError,
  sanitizeCallRailErrorMessage,
  type CallRailApiError,
} from "./errors";
import { safeFetchCallRail } from "./safe-fetch";
import {
  CALLRAIL_GRANULARITY_VALUES,
  CALLRAIL_GROUP_BY_VALUES,
  CALLRAIL_SUMMARY_FILTER_KEYS,
  CALLRAIL_TIMESERIES_MAX_POINTS,
  computeCallRailFields,
} from "./fields";
import {
  transformCallRailCallRow,
  transformCallRailFormSubmissionRow,
  transformCallRailSummaryRow,
  transformCallRailTextMessageRow,
  transformCallRailTimeseriesRow,
} from "./transformers";

/** Base URL — frozen. */
export const CALLRAIL_API_BASE = "https://api.callrail.com/v3" as const;

/** Per-page maximum across `/calls.json` and `/form_submissions.json`. */
export const CALLRAIL_PER_PAGE_MAX = 250;

// ============================================================================
// Account discovery — AC #2
// ============================================================================

/**
 * Single CallRail account as returned by `/v3/a.json`. The docs document
 * additional fields (`outbound_recording_enabled`, `hipaa_account`,
 * `numeric_id`, `created_at`, etc.) — we surface the canonical 4 for the
 * picker UI and let the route handler stash the full shape in KV side-channel
 * if needed.
 */
export interface CallRailAccount {
  readonly id: string;
  readonly name: string;
  readonly outboundRecordingEnabled?: boolean;
  readonly hipaaAccount?: boolean;
}

export interface DiscoverCallRailAccountsResult {
  readonly accounts: ReadonlyArray<CallRailAccount>;
  readonly totalRecords: number;
}

/**
 * Discover accessible CallRail accounts for the given API token. The single
 * canonical entitlement-probe call — `GET /v3/a.json` costs nothing extra
 * beyond a single rate-limit-quota request (CallRail bills only by request
 * count, not per-row).
 *
 * @param apiToken - The plaintext API token.
 * @param options - Optional fetch override (for tests).
 * @throws `CallRailApiCallError` on HTTP error.
 */
/** Maximum pages to walk for account discovery. 20 pages × 250/page = 5,000 accounts, bounded against pathological responses. */
const CALLRAIL_DISCOVER_ACCOUNTS_MAX_PAGES = 20;

export async function discoverCallRailAccounts(
  apiToken: string,
  options: { readonly fetchOverride?: typeof fetch } = {},
): Promise<DiscoverCallRailAccountsResult> {
  void options; // No-op — `safeFetchCallRail` uses the global fetch.

  // Pass 1 review fix: previously only the first page (per_page=250)
  // was fetched. Real reseller / agency CallRail accounts have N>250
  // sub-accounts; later pages were silently truncated, breaking the
  // `allowedAccountIds` cross-check on rotation. Now we walk
  // `has_next_page` up to `CALLRAIL_DISCOVER_ACCOUNTS_MAX_PAGES` to
  // cover up to 5,000 accounts while keeping memory bounded.
  const accounts: CallRailAccount[] = [];
  let totalRecords = 0;
  let page = 1;
  let walkedPages = 0;
  while (walkedPages < CALLRAIL_DISCOVER_ACCOUNTS_MAX_PAGES) {
    walkedPages++;
    const url = `${CALLRAIL_API_BASE}/a.json?per_page=250&page=${page}`;
    const result = await safeFetchCallRail(url, apiToken, { method: "GET" });
    if (!result.success) {
      // `safeFetchCallRail` returns a structured error — wrap it.
      throw new CallRailApiCallError({
        type: "unknown",
        message: result.error.message,
        status: result.error.providerStatus ?? null,
        retryable: result.error.retryable,
      });
    }

    const response = result.data;
    if (!response.ok) {
      const body = await safeReadBody(response);
      const parsed = parseCallRailApiError(response.status, body, apiToken);
      throw new CallRailApiCallError(parsed);
    }

    const body = await safeReadJson(response);
    if (!body || !Array.isArray((body as { accounts?: unknown }).accounts)) {
      throw new CallRailApiCallError({
        type: "unknown",
        message: sanitizeCallRailErrorMessage(
          "CallRail /v3/a.json response missing `accounts` array",
          apiToken,
        ),
        status: response.status,
        retryable: false,
      });
    }

    const raw = body as {
      accounts: ReadonlyArray<Record<string, unknown>>;
      total_records?: number;
      total_pages?: number;
      has_next_page?: boolean;
      next_page?: number | string | null;
    };

    if (typeof raw.total_records === "number") {
      totalRecords = raw.total_records;
    }

    // Pass 2 review fix: dedupe by id (defense against cursor-loop
    // bugs returning the same accounts page after page), AND skip
    // empty-id rows (a CallRail response anomaly would otherwise admit
    // `id: ""` into the allowlist where it would 404 on every
    // downstream request).
    const seenIds = new Set(accounts.map((a) => a.id));
    let pageAdditions = 0;
    for (const a of raw.accounts) {
      const id = String(a["id"] ?? "");
      if (id.length === 0) continue;
      if (seenIds.has(id)) continue;
      seenIds.add(id);
      pageAdditions++;
      accounts.push({
        id,
        name: String(a["name"] ?? ""),
        outboundRecordingEnabled:
          typeof a["outbound_recording_enabled"] === "boolean"
            ? (a["outbound_recording_enabled"] as boolean)
            : undefined,
        hipaaAccount:
          typeof a["hipaa_account"] === "boolean"
            ? (a["hipaa_account"] as boolean)
            : undefined,
      });
    }
    // If a page returned nothing new despite claiming more pages, break
    // to avoid infinite cursor loops on misbehaving APIs.
    if (pageAdditions === 0) break;

    // Decide whether to fetch the next page. Prefer
    // `has_next_page` boolean when present, else compare
    // `total_pages` against the current page number, else stop when
    // the latest page came back smaller than the per_page request.
    const hasNext =
      typeof raw.has_next_page === "boolean"
        ? raw.has_next_page
        : typeof raw.total_pages === "number"
          ? page < raw.total_pages
          : raw.accounts.length === 250;
    if (!hasNext) break;
    page++;
  }

  if (totalRecords === 0) totalRecords = accounts.length;
  return { accounts, totalRecords };
}

/**
 * Connection-validation helper. Calls `discoverCallRailAccounts` and
 * returns a typed result. Caller (route or health-check) handles the
 * zero-accounts case + decides what to do with the discovered list.
 */
export async function validateCallRailConnection(
  apiToken: string,
): Promise<{ readonly ok: true; readonly accounts: ReadonlyArray<CallRailAccount>; readonly totalRecords: number }> {
  const { accounts, totalRecords } = await discoverCallRailAccounts(apiToken);
  return { ok: true, accounts, totalRecords };
}

// ============================================================================
// Conversation Intelligence entitlement probe — AC #2
// ============================================================================

export type CallRailCiEntitlement = "entitled" | "not_entitled" | "unknown";

export interface ProbeCallRailCiResult {
  readonly entitlement: CallRailCiEntitlement;
  readonly status: number;
}

/**
 * Probe whether the customer has Conversation Intelligence entitled.
 *
 * Implementation: call `GET /v3/a/{firstAccountId}/calls.json?per_page=1&fields=sentiment,transcription`.
 *  - 200 with `calls[0].sentiment` is a string → `entitled`
 *  - 200 with `calls.length === 0` → `unknown` (no data to test against)
 *  - 200 with `calls[0].sentiment === null` for populated calls → `not_entitled`
 *  - 403 / 422 → `not_entitled` (subscription gate)
 *  - Any other error → re-throws via `CallRailApiCallError`
 *
 * Per `support.callrail.com/hc/en-us/articles/35110502230157-Getting-started-with-Premium-Conversation-Intelligence`
 * verified 2026-05-14.
 */
export async function probeCallRailConversationIntelligence(
  apiToken: string,
  accountId: string,
): Promise<ProbeCallRailCiResult> {
  const url = `${CALLRAIL_API_BASE}/a/${encodeURIComponent(accountId)}/calls.json?per_page=1&fields=sentiment,transcription`;
  const result = await safeFetchCallRail(url, apiToken, { method: "GET" });
  if (!result.success) {
    // Network/transport error — return unknown rather than throwing, so the
    // probe doesn't break the whole connect flow on a transient hiccup.
    return { entitlement: "unknown", status: 0 };
  }
  const response = result.data;
  if (response.status === 403 || response.status === 422) {
    return { entitlement: "not_entitled", status: response.status };
  }
  if (!response.ok) {
    const body = await safeReadBody(response);
    const parsed = parseCallRailApiError(response.status, body, apiToken);
    // For 4xx errors not in the gate list (401 = token invalid, 404 = account
    // not in allowlist), re-throw so the route handler surfaces them.
    throw new CallRailApiCallError(parsed);
  }
  const body = await safeReadJson(response);
  const calls = (body as { calls?: ReadonlyArray<Record<string, unknown>> })?.calls;
  if (!Array.isArray(calls) || calls.length === 0) {
    // No call data to test — keep adapter optimistic, defer to first sync.
    return { entitlement: "unknown", status: response.status };
  }
  const first = calls[0];
  // Pass 1 review fix: previously, ANY non-string sentiment value (null,
  // undefined, missing key) was classified as `not_entitled`. But on a
  // CI-entitled account whose newest call hasn't had sentiment computed
  // yet (CI processing lags after call end), sentiment is `null` even
  // though the customer IS entitled. Misclassifying as `not_entitled`
  // would permanently strip CI fields from sync requests until reconnect.
  // Treat a non-empty string as `entitled`; treat ANY other non-string
  // value as `unknown` (the 403/422 branch above is the authoritative
  // `not_entitled` signal).
  if (
    first &&
    typeof first["sentiment"] === "string" &&
    (first["sentiment"] as string).length > 0
  ) {
    return { entitlement: "entitled", status: response.status };
  }
  return { entitlement: "unknown", status: response.status };
}

// ============================================================================
// Endpoint helpers — `/calls/summary.json` etc.
// ============================================================================

export interface FetchCallRailCallsSummaryParams {
  readonly apiToken: string;
  readonly accountId: string;
  readonly companyId?: string | null;
  readonly startDate: string; // ISO 8601 (YYYY-MM-DD)
  readonly endDate: string;
  readonly groupBy?: string;
  /** Optional filter params per `CALLRAIL_SUMMARY_FILTER_KEYS`. */
  readonly filters?: Readonly<Record<string, string | number>>;
}

export interface CallRailSummaryResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly rowsCharged: number;
}

/**
 * `GET /v3/a/{account_id}/calls/summary.json`
 *
 * Returns ONE row when `groupBy` is omitted, or N rows when grouped.
 *
 * **Round 5 Edge Case #2 invariant:** when the integration's `companyId`
 * filter is active (i.e. `params.companyId` is set) AND `groupBy ===
 * "company"`, the call collapses to a single bucket — reject synchronously
 * with `TypeError` at the helper level. The integration's company filter
 * already does the company-scoping; double-scoping is a widget-config
 * bug we surface early.
 */
export async function fetchCallRailCallsSummary(
  params: FetchCallRailCallsSummaryParams,
): Promise<CallRailSummaryResponse> {
  requireDateRange(params.startDate, params.endDate);
  if (params.groupBy !== undefined) {
    if (!CALLRAIL_GROUP_BY_VALUES.has(params.groupBy)) {
      throw new TypeError(
        `fetchCallRailCallsSummary: invalid groupBy "${params.groupBy}". Expected one of: ${[...CALLRAIL_GROUP_BY_VALUES].join(", ")}`,
      );
    }
    if (
      (params.groupBy === "company" || params.groupBy === "company_id") &&
      params.companyId !== undefined &&
      params.companyId !== null
    ) {
      throw new TypeError(
        `fetchCallRailCallsSummary: groupBy="company" is invalid when companyId filter is active (the company filter already scopes the response to one company; grouping by company would collapse to a single bucket).`,
      );
    }
  }

  const url = buildCallRailSummaryUrl(params);
  const result = await safeFetchCallRail(url, params.apiToken, { method: "GET" });
  if (!result.success) {
    throw wrapTransportError(result.error, params.apiToken);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    throw new CallRailApiCallError(
      parseCallRailApiError(response.status, body, params.apiToken),
    );
  }
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CallRailApiCallError({
      type: "unknown",
      message: sanitizeCallRailErrorMessage(
        "CallRail /calls/summary.json response was empty or not JSON",
        params.apiToken,
      ),
      status: response.status,
      retryable: false,
    });
  }

  const records: Record<string, unknown>[] = [];
  const grouped = body["grouped_results"];
  const total = body["total_results"];

  if (Array.isArray(grouped)) {
    for (const row of grouped) {
      if (row && typeof row === "object") {
        records.push(
          transformCallRailSummaryRow(row as Record<string, unknown>, {
            accountId: params.accountId,
            companyId: params.companyId ?? null,
            groupByValue:
              typeof (row as Record<string, unknown>)["key"] === "string"
                ? ((row as Record<string, unknown>)["key"] as string)
                : null,
          }),
        );
      }
    }
  } else if (total && typeof total === "object") {
    records.push(
      transformCallRailSummaryRow(total as Record<string, unknown>, {
        accountId: params.accountId,
        companyId: params.companyId ?? null,
      }),
    );
  }
  // If both keys are missing, return an empty records array (Round 5 Edge
  // Case #3 — empty-result resilience).
  return { records, rowsCharged: 1 };
}

export interface FetchCallRailCallsTimeseriesParams {
  readonly apiToken: string;
  readonly accountId: string;
  readonly companyId?: string | null;
  readonly startDate: string;
  readonly endDate: string;
  readonly granularity?: string;
  readonly groupBy?: string;
}

export interface CallRailTimeseriesResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly rowsCharged: number;
  readonly warnings: ReadonlyArray<string>;
}

/**
 * `GET /v3/a/{account_id}/calls/timeseries.json`
 *
 * Returns N rows where each row is one interval (day/week/month/etc per
 * `granularity`). **Capped at 200 data points per response** per
 * `apidocs.callrail.com/` verified 2026-05-14 — if the requested
 * granularity/date-range combination would exceed this, surface a
 * warning so the caller can chunk.
 *
 * Default `granularity` is `day` when omitted (per the docs).
 */
export async function fetchCallRailCallsTimeseries(
  params: FetchCallRailCallsTimeseriesParams,
): Promise<CallRailTimeseriesResponse> {
  requireDateRange(params.startDate, params.endDate);
  const granularity = params.granularity ?? "day";
  if (!CALLRAIL_GRANULARITY_VALUES.has(granularity)) {
    throw new TypeError(
      `fetchCallRailCallsTimeseries: invalid granularity "${granularity}". Expected one of: ${[...CALLRAIL_GRANULARITY_VALUES].join(", ")}`,
    );
  }
  if (params.groupBy !== undefined) {
    if (!CALLRAIL_GROUP_BY_VALUES.has(params.groupBy)) {
      throw new TypeError(
        `fetchCallRailCallsTimeseries: invalid groupBy "${params.groupBy}". Expected one of: ${[...CALLRAIL_GROUP_BY_VALUES].join(", ")}`,
      );
    }
    if (
      (params.groupBy === "company" || params.groupBy === "company_id") &&
      params.companyId !== undefined &&
      params.companyId !== null
    ) {
      throw new TypeError(
        `fetchCallRailCallsTimeseries: groupBy="company" is invalid when companyId filter is active.`,
      );
    }
  }

  const url = buildCallRailTimeseriesUrl({ ...params, granularity });

  // Pre-flight: estimate row count against the 200-cap and surface a warning.
  const warnings: string[] = [];
  const estimatedPoints = estimateTimeseriesPoints(
    params.startDate,
    params.endDate,
    granularity,
  );
  if (estimatedPoints > CALLRAIL_TIMESERIES_MAX_POINTS) {
    warnings.push(
      `CallRail timeseries response is capped at ${CALLRAIL_TIMESERIES_MAX_POINTS} data points per request; estimated ${estimatedPoints} for granularity="${granularity}" over the requested range. Consider coarser granularity or chunking the date range.`,
    );
  }

  const result = await safeFetchCallRail(url, params.apiToken, { method: "GET" });
  if (!result.success) {
    throw wrapTransportError(result.error, params.apiToken);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    throw new CallRailApiCallError(
      parseCallRailApiError(response.status, body, params.apiToken),
    );
  }
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CallRailApiCallError({
      type: "unknown",
      message: sanitizeCallRailErrorMessage(
        "CallRail /calls/timeseries.json response was empty or not JSON",
        params.apiToken,
      ),
      status: response.status,
      retryable: false,
    });
  }

  // CallRail's timeseries response wraps the array under different key names
  // across endpoints — docs show `data`, but some changelog entries reference
  // `timeseries`. Probe both defensively.
  const tsArray = Array.isArray(body["data"])
    ? (body["data"] as ReadonlyArray<Record<string, unknown>>)
    : Array.isArray(body["timeseries"])
      ? (body["timeseries"] as ReadonlyArray<Record<string, unknown>>)
      : [];

  const records = tsArray.map((row) =>
    transformCallRailTimeseriesRow(row, {
      accountId: params.accountId,
      companyId: params.companyId ?? null,
      granularity,
    }),
  );
  return { records, rowsCharged: 1, warnings };
}

export interface FetchCallRailCallsParams {
  readonly apiToken: string;
  readonly accountId: string;
  readonly companyId?: string | null;
  readonly startDate: string;
  readonly endDate: string;
  /** Default 100; clamped to `CALLRAIL_PER_PAGE_MAX` (250) at the helper. */
  readonly perPage?: number;
  /**
   * 1-indexed page number for pagination. CallRail's API uses
   * `?page=N` per `apidocs.callrail.com/` verified 2026-05-14.
   * Pass 3 review fix: the paginating wrapper (`fetchAllCallRailCalls`)
   * was previously a no-op because no page bump was wired into the
   * URL builder — every iteration fetched page 1.
   */
  readonly page?: number;
  readonly relativePagination?: boolean;
  readonly conversationIntelligenceEntitled: boolean;
}

export interface CallRailCallsResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly rowsCharged: number;
  readonly hasNextPage: boolean;
  readonly nextPage: string | null;
}

/**
 * `GET /v3/a/{account_id}/calls.json`
 *
 * Returns paginated per-call records. The transformer STRIPS recording URLs
 * (Round 3 Red Team Attack #2). PII fields flow through to the persisted
 * record shape (tenant-scoped); log emissions must run
 * `sanitizeCallRailPii` before output.
 *
 * Field selection: when `conversationIntelligenceEntitled === true`, the
 * full 38-field allowlist is requested; when false, the 9 CI-gated fields
 * are stripped (per AC #4 + `computeCallRailFields`).
 */
export async function fetchCallRailCalls(
  params: FetchCallRailCallsParams,
): Promise<CallRailCallsResponse> {
  requireDateRange(params.startDate, params.endDate);
  const perPage = clampPerPage(params.perPage ?? 100);
  const url = buildCallRailCallsUrl({ ...params, perPage });
  const result = await safeFetchCallRail(url, params.apiToken, { method: "GET" });
  if (!result.success) {
    throw wrapTransportError(result.error, params.apiToken);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    throw new CallRailApiCallError(
      parseCallRailApiError(response.status, body, params.apiToken),
    );
  }
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CallRailApiCallError({
      type: "unknown",
      message: sanitizeCallRailErrorMessage(
        "CallRail /calls.json response was empty or not JSON",
        params.apiToken,
      ),
      status: response.status,
      retryable: false,
    });
  }
  const calls = Array.isArray(body["calls"])
    ? (body["calls"] as ReadonlyArray<Record<string, unknown>>)
    : [];
  const records = calls.map((row) =>
    transformCallRailCallRow(row, {
      accountId: params.accountId,
      companyId: params.companyId ?? null,
    }),
  );
  return {
    records,
    rowsCharged: 1,
    hasNextPage: Boolean(body["has_next_page"]),
    nextPage:
      typeof body["next_page"] === "string"
        ? (body["next_page"] as string)
        : null,
  };
}

/**
 * Maximum pages to walk for data-bearing endpoints. 50 pages × 250
 * per_page = 12,500 records, bounded against pathological responses
 * (cursor loops, off-by-one infinite pagination, etc).
 *
 * Pass 2 review fix: previously the adapter called `fetchCallRailCalls`
 * ONCE per sync and dropped everything past the first 100 records.
 * Real agencies have >100 calls in a date range — the cap was
 * silently truncating the headline metric (`totalCalls` derived from
 * /calls.json record count) for any non-trivial customer.
 */
const CALLRAIL_DATA_MAX_PAGES = 50;

/**
 * Drain `/calls.json` pagination up to `CALLRAIL_DATA_MAX_PAGES`.
 * Returns the union of all records across pages plus a `truncated`
 * flag if the cap was hit.
 *
 * Pass 2 review fix (Critical High data-loss): wraps the per-page
 * `fetchCallRailCalls` helper and walks `has_next_page` / `next_page`
 * until exhausted or capped.
 */
export async function fetchAllCallRailCalls(
  params: FetchCallRailCallsParams,
): Promise<{
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly truncated: boolean;
  readonly pagesWalked: number;
}> {
  const seenIds = new Set<string>();
  const aggregated: Record<string, unknown>[] = [];
  // Use per_page=250 (the API max) for fewer round trips; caller's
  // perPage is preserved as a per-call ceiling.
  const perPage = clampPerPage(params.perPage ?? 250);
  let pagesWalked = 0;
  // Pass 3 review fix: emit a real `?page=N` query param each
  // iteration. Previous "fix" set `relativePagination: true` without
  // sending a cursor token, which made every iteration refetch page 1
  // (the seenIds dedup masked the bug but it still burned 50× API
  // quota per sync).
  for (let pageNum = 1; pageNum <= CALLRAIL_DATA_MAX_PAGES; pageNum++) {
    pagesWalked++;
    const page = await fetchCallRailCalls({ ...params, perPage, page: pageNum });
    let pageAdditions = 0;
    for (const r of page.records) {
      // Pass 3 review fix: String-coerce so numeric / null IDs still
      // get deduped (CallRail's docs aren't strict on id type across
      // endpoints; defense in depth).
      const id = String(r["id"] ?? "");
      if (id.length > 0 && seenIds.has(id)) continue;
      if (id.length > 0) seenIds.add(id);
      pageAdditions++;
      aggregated.push(r);
    }
    if (!page.hasNextPage) break;
    // Defense against cursor-loops / misbehaving servers that claim
    // hasNextPage=true but return no new records: break to avoid
    // wasted quota burn.
    if (pageAdditions === 0) break;
  }
  return {
    records: aggregated,
    truncated: pagesWalked >= CALLRAIL_DATA_MAX_PAGES,
    pagesWalked,
  };
}

export interface FetchCallRailFormSubmissionsParams {
  readonly apiToken: string;
  readonly accountId: string;
  readonly companyId?: string | null;
  readonly startDate: string;
  readonly endDate: string;
  readonly perPage?: number;
  /** 1-indexed page number. Pass 3 review fix. */
  readonly page?: number;
  readonly firstFormOnly?: boolean;
}

export interface FetchCallRailTextMessagesParams {
  readonly apiToken: string;
  readonly accountId: string;
  readonly companyId?: string | null;
  readonly startDate: string;
  readonly endDate: string;
  readonly perPage?: number;
  /** 1-indexed page number. Pass 3 review fix. */
  readonly page?: number;
}

export interface CallRailTextMessagesResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly rowsCharged: number;
  readonly hasNextPage: boolean;
}

export interface CallRailFormSubmissionsResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly rowsCharged: number;
  readonly hasNextPage: boolean;
}

/**
 * `GET /v3/a/{account_id}/form_submissions.json`
 *
 * Returns paginated form-submission records. CRITICAL for local-services
 * agencies (HVAC/legal) where form fills are the OTHER lead channel besides
 * calls.
 */
export async function fetchCallRailFormSubmissions(
  params: FetchCallRailFormSubmissionsParams,
): Promise<CallRailFormSubmissionsResponse> {
  requireDateRange(params.startDate, params.endDate);
  const perPage = clampPerPage(params.perPage ?? 100);
  const url = buildCallRailFormSubmissionsUrl({ ...params, perPage });
  const result = await safeFetchCallRail(url, params.apiToken, { method: "GET" });
  if (!result.success) {
    throw wrapTransportError(result.error, params.apiToken);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    throw new CallRailApiCallError(
      parseCallRailApiError(response.status, body, params.apiToken),
    );
  }
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CallRailApiCallError({
      type: "unknown",
      message: sanitizeCallRailErrorMessage(
        "CallRail /form_submissions.json response was empty or not JSON",
        params.apiToken,
      ),
      status: response.status,
      retryable: false,
    });
  }
  const forms = Array.isArray(body["form_submissions"])
    ? (body["form_submissions"] as ReadonlyArray<Record<string, unknown>>)
    : [];
  const records = forms.map((row) =>
    transformCallRailFormSubmissionRow(row, {
      accountId: params.accountId,
      companyId: params.companyId ?? null,
    }),
  );
  return {
    records,
    rowsCharged: 1,
    hasNextPage: Boolean(body["has_next_page"]),
  };
}

/**
 * Drain `/form_submissions.json` pagination up to
 * `CALLRAIL_DATA_MAX_PAGES`. Same Pass 2 fix as `fetchAllCallRailCalls`.
 */
export async function fetchAllCallRailFormSubmissions(
  params: FetchCallRailFormSubmissionsParams,
): Promise<{
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly truncated: boolean;
  readonly pagesWalked: number;
}> {
  const seenIds = new Set<string>();
  const aggregated: Record<string, unknown>[] = [];
  const perPage = clampPerPage(params.perPage ?? 250);
  let pagesWalked = 0;
  // Pass 3 review fix: same as fetchAllCallRailCalls — emit real
  // `?page=N`, String-coerce IDs, break on no-progress.
  for (let pageNum = 1; pageNum <= CALLRAIL_DATA_MAX_PAGES; pageNum++) {
    pagesWalked++;
    const page = await fetchCallRailFormSubmissions({
      ...params,
      perPage,
      page: pageNum,
    });
    let pageAdditions = 0;
    for (const r of page.records) {
      const id = String(r["id"] ?? "");
      if (id.length > 0 && seenIds.has(id)) continue;
      if (id.length > 0) seenIds.add(id);
      pageAdditions++;
      aggregated.push(r);
    }
    if (!page.hasNextPage) break;
    if (pageAdditions === 0) break;
  }
  return {
    records: aggregated,
    truncated: pagesWalked >= CALLRAIL_DATA_MAX_PAGES,
    pagesWalked,
  };
}

/**
 * `GET /v3/a/{account_id}/text-messages.json`
 *
 * Returns paginated SMS conversation records. Used to derive the SMS
 * metrics (Total SMS Conversations, First Text, Last Message Time). The
 * helper preserves the raw record shape and lets the consumer (adapter or
 * widget) compute aggregates. Records get `_reportType="text-messages"`.
 *
 * **NOTE on quotas (per `rollout.com/integration-guides/call-rail/api-essentials`
 * verified 2026-05-14):** SMS-SEND has a separate 150/hr + 1K/day quota.
 * READING SMS history via this endpoint counts against the general
 * 1K/hr + 10K/day API quota (this endpoint is a READ, not a SEND).
 */
export async function fetchCallRailTextMessages(
  params: FetchCallRailTextMessagesParams,
): Promise<CallRailTextMessagesResponse> {
  requireDateRange(params.startDate, params.endDate);
  const perPage = clampPerPage(params.perPage ?? 100);
  const qs = new URLSearchParams();
  qs.set("start_date", params.startDate);
  qs.set("end_date", params.endDate);
  qs.set("per_page", String(perPage));
  // Pass 3 review fix: emit `?page=N` so the paginating wrapper can
  // actually advance.
  if (typeof params.page === "number" && params.page > 0) {
    qs.set("page", String(params.page));
  }
  appendCompanyIdQuery(qs, params.companyId);
  const url = `${CALLRAIL_API_BASE}/a/${encodeURIComponent(params.accountId)}/text-messages.json?${qs.toString()}`;
  const result = await safeFetchCallRail(url, params.apiToken, { method: "GET" });
  if (!result.success) {
    throw wrapTransportError(result.error, params.apiToken);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    throw new CallRailApiCallError(
      parseCallRailApiError(response.status, body, params.apiToken),
    );
  }
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CallRailApiCallError({
      type: "unknown",
      message: sanitizeCallRailErrorMessage(
        "CallRail /text-messages.json response was empty or not JSON",
        params.apiToken,
      ),
      status: response.status,
      retryable: false,
    });
  }
  const messages = Array.isArray(body["text_messages"])
    ? (body["text_messages"] as ReadonlyArray<Record<string, unknown>>)
    : [];
  // Pass 1 review fix: route SMS rows through the shared transformer
  // so they get CSV-injection sanitization on every string field,
  // consistent with calls / form_submissions rows.
  const records = messages.map((row) =>
    transformCallRailTextMessageRow(row, {
      accountId: params.accountId,
      companyId: params.companyId ?? null,
    }),
  );
  return {
    records,
    rowsCharged: 1,
    hasNextPage: Boolean(body["has_next_page"]),
  };
}

/**
 * Drain `/text-messages.json` pagination up to `CALLRAIL_DATA_MAX_PAGES`.
 * Same Pass 2 fix as `fetchAllCallRailCalls`.
 */
export async function fetchAllCallRailTextMessages(
  params: FetchCallRailTextMessagesParams,
): Promise<{
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly truncated: boolean;
  readonly pagesWalked: number;
}> {
  const seenIds = new Set<string>();
  const aggregated: Record<string, unknown>[] = [];
  const perPage = clampPerPage(params.perPage ?? 250);
  let pagesWalked = 0;
  // Pass 3 review fix.
  for (let pageNum = 1; pageNum <= CALLRAIL_DATA_MAX_PAGES; pageNum++) {
    pagesWalked++;
    const page = await fetchCallRailTextMessages({
      ...params,
      perPage,
      page: pageNum,
    });
    let pageAdditions = 0;
    for (const r of page.records) {
      const id = String(r["id"] ?? "");
      if (id.length > 0 && seenIds.has(id)) continue;
      if (id.length > 0) seenIds.add(id);
      pageAdditions++;
      aggregated.push(r);
    }
    if (!page.hasNextPage) break;
    if (pageAdditions === 0) break;
  }
  return {
    records: aggregated,
    truncated: pagesWalked >= CALLRAIL_DATA_MAX_PAGES,
    pagesWalked,
  };
}

// ============================================================================
// URL builders
// ============================================================================

/**
 * **Web research divergence note:** the story's AC #4 specifies a
 * company-scoped path shape `/v3/a/{account_id}/c/{company_id}/...`. The
 * actual CallRail v3 API uses `?company_id=<id>` as a query parameter on
 * the account-scoped path (verified `apidocs.callrail.com/` 2026-05-14
 * — the docs explicitly describe `company_id` as a filter parameter:
 * *"If provided, only return calls to tracking numbers belonging to this
 * company."*). Per the user's Option-1 decision, we implement against
 * the actual API shape and document the divergence in story Completion
 * Notes.
 */
function appendCompanyIdQuery(
  params: URLSearchParams,
  companyId?: string | null,
): void {
  if (companyId !== undefined && companyId !== null && companyId.length > 0) {
    params.set("company_id", companyId);
  }
}

function buildCallRailSummaryUrl(p: FetchCallRailCallsSummaryParams): string {
  const qs = new URLSearchParams();
  qs.set("start_date", p.startDate);
  qs.set("end_date", p.endDate);
  if (p.groupBy) qs.set("group_by", p.groupBy);
  appendCompanyIdQuery(qs, p.companyId);
  if (p.filters) {
    for (const [k, v] of Object.entries(p.filters)) {
      if (!CALLRAIL_SUMMARY_FILTER_KEYS.has(k)) {
        throw new TypeError(
          `fetchCallRailCallsSummary: unknown filter key "${k}". Expected one of: ${[...CALLRAIL_SUMMARY_FILTER_KEYS].join(", ")}`,
        );
      }
      qs.set(k, String(v));
    }
  }
  return `${CALLRAIL_API_BASE}/a/${encodeURIComponent(p.accountId)}/calls/summary.json?${qs.toString()}`;
}

function buildCallRailTimeseriesUrl(
  p: FetchCallRailCallsTimeseriesParams & { granularity: string },
): string {
  const qs = new URLSearchParams();
  qs.set("start_date", p.startDate);
  qs.set("end_date", p.endDate);
  qs.set("granularity", p.granularity);
  if (p.groupBy) qs.set("group_by", p.groupBy);
  appendCompanyIdQuery(qs, p.companyId);
  return `${CALLRAIL_API_BASE}/a/${encodeURIComponent(p.accountId)}/calls/timeseries.json?${qs.toString()}`;
}

function buildCallRailCallsUrl(
  p: FetchCallRailCallsParams & { perPage: number },
): string {
  const qs = new URLSearchParams();
  qs.set("start_date", p.startDate);
  qs.set("end_date", p.endDate);
  qs.set("per_page", String(p.perPage));
  qs.set("fields", computeCallRailFields(p.conversationIntelligenceEntitled));
  // Pass 3 review fix: emit `?page=N` so the paginating wrapper can
  // actually advance past page 1.
  if (typeof p.page === "number" && p.page > 0) {
    qs.set("page", String(p.page));
  }
  if (p.relativePagination) qs.set("relative_pagination", "true");
  appendCompanyIdQuery(qs, p.companyId);
  return `${CALLRAIL_API_BASE}/a/${encodeURIComponent(p.accountId)}/calls.json?${qs.toString()}`;
}

function buildCallRailFormSubmissionsUrl(
  p: FetchCallRailFormSubmissionsParams & { perPage: number },
): string {
  const qs = new URLSearchParams();
  qs.set("start_date", p.startDate);
  qs.set("end_date", p.endDate);
  qs.set("per_page", String(p.perPage));
  // Pass 3 review fix.
  if (typeof p.page === "number" && p.page > 0) {
    qs.set("page", String(p.page));
  }
  if (p.firstFormOnly) qs.set("first_form", "true");
  appendCompanyIdQuery(qs, p.companyId);
  return `${CALLRAIL_API_BASE}/a/${encodeURIComponent(p.accountId)}/form_submissions.json?${qs.toString()}`;
}

// ============================================================================
// Internal helpers
// ============================================================================

function requireDateRange(start: string, end: string): void {
  if (!start || !end) {
    throw new TypeError(
      `CallRail helpers require explicit start_date and end_date (ISO 8601 YYYY-MM-DD). Named ranges like "today" or "last_30_days" are NOT used — pre-resolve to absolute dates at the caller (queue scheduler operates in UTC).`,
    );
  }
}

function clampPerPage(requested: number): number {
  if (requested < 1) return 1;
  if (requested > CALLRAIL_PER_PAGE_MAX) {
    throw new TypeError(
      `CallRail per_page maximum is ${CALLRAIL_PER_PAGE_MAX}; requested ${requested}.`,
    );
  }
  return Math.floor(requested);
}

/**
 * Estimate how many time-series buckets a `[startDate, endDate]` range
 * produces at a given granularity. Used to surface the 200-point cap
 * warning in `fetchCallRailCallsTimeseries`.
 *
 * Conservative: rounds UP so we err toward warning the caller rather
 * than silently truncating. Works on ISO date strings; the parser is
 * UTC-anchored to match CallRail's date semantics.
 */
function estimateTimeseriesPoints(
  startDate: string,
  endDate: string,
  granularity: string,
): number {
  const startMs = Date.parse(`${startDate}T00:00:00Z`);
  const endMs = Date.parse(`${endDate}T23:59:59Z`);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
    return 0;
  }
  const diffMs = endMs - startMs;
  const hour = 3_600_000;
  const day = 24 * hour;
  switch (granularity) {
    case "hour":
      return Math.ceil(diffMs / hour);
    case "day":
      return Math.ceil(diffMs / day);
    case "week":
      return Math.ceil(diffMs / (7 * day));
    case "month":
      return Math.ceil(diffMs / (30 * day));
    case "year":
      return Math.ceil(diffMs / (365 * day));
    default:
      return Math.ceil(diffMs / day);
  }
}

async function safeReadBody(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function wrapTransportError(
  err: { readonly message: string; readonly retryable: boolean; readonly providerStatus?: number },
  apiToken: string,
): CallRailApiCallError {
  const parsed: CallRailApiError = {
    type: "unknown",
    message: sanitizeCallRailErrorMessage(err.message, apiToken),
    status: typeof err.providerStatus === "number" ? err.providerStatus : null,
    retryable: err.retryable,
  };
  return new CallRailApiCallError(parsed);
}

// ============================================================================
// Story 22.5 — Recording-related helpers (AC #14, AC #4 step "callrail" branch)
// ============================================================================

/**
 * Probe whether the customer's CallRail account has call recording enabled at
 * the ACCOUNT level (the master switch in CallRail's Account Settings →
 * "Call Settings"). Per `apidocs.callrail.com/` verified 2026-05-16, the
 * `/v3/a/{accountId}.json` endpoint returns the account's full settings
 * including a `recording_enabled` boolean.
 *
 * **Return contract:**
 *   - 200 with `recording_enabled === true` → returns `true`
 *   - 200 with `recording_enabled === false` (or missing) → returns `false`
 *   - 401 / 404 → throws `CallRailApiCallError` (the caller — validateConnection
 *     wiring in callrail.ts — re-routes 401 to the standard token-expired
 *     flow; 404 should never happen because the discovered account list comes
 *     from `discoverCallRailAccounts` itself)
 *   - 5xx / transport error → throws via `wrapTransportError` (transient
 *     failures bubble to the route handler; the metadata stamp is left
 *     unchanged from the previous probe)
 *
 * Story 22.5 AC #14.
 */
export async function probeCallRailRecordingsEnabled(
  apiToken: string,
  accountId: string,
): Promise<boolean> {
  const url = `${CALLRAIL_API_BASE}/a/${encodeURIComponent(accountId)}.json`;
  const result = await safeFetchCallRail(url, apiToken, { method: "GET" });
  if (!result.success) {
    throw wrapTransportError(result.error, apiToken);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    const parsed = parseCallRailApiError(response.status, body, apiToken);
    throw new CallRailApiCallError(parsed);
  }
  const body = (await safeReadJson(response)) as
    | { recording_enabled?: unknown }
    | null;
  // Strict boolean check — any non-boolean value (null, missing, "yes",
  // "true" as string) defaults to FALSE. This is the conservative default:
  // if we can't confirm recording is enabled, we DON'T render the Play
  // button on the widget.
  return body?.recording_enabled === true;
}

/**
 * Fetch the upstream signed recording URL for a single call.
 *
 * **CRITICAL — this is the only helper in `callrail/api.ts` that touches the
 * recording endpoint.** The stream-route at
 * `apps/server/src/routes/recordings/stream.ts` is the ONLY route allowed
 * to call this helper. CI grep guards (AC #21) enforce this constraint
 * across the rest of the codebase.
 *
 * Per `apidocs.callrail.com/` verified 2026-05-16, the endpoint is
 *   `GET /v3/a/{account_id}/calls/{call_id}/recording.json`
 * and returns a JSON body of shape
 *   `{ url: "https://hosted-recording-callrail.s3.amazonaws.com/...?Signature=...&Expires=..." }`
 *
 * The returned URL is a temporary CallRail-hosted asset URL with a 24-hour
 * upstream TTL. ProvenLeap does NOT cache this URL — every recording-stream
 * request mints a fresh upstream URL via this helper.
 *
 * **Return contract:**
 *   - 200 with non-empty `url` string → returns `{ url }`
 *   - 200 with missing/empty `url` field → throws (mapped to 404 by caller)
 *   - 401 → throws (mapped to 502 UPSTREAM_AUTH_FAILED by caller)
 *   - 404 → throws (mapped to 404 RECORDING_NOT_FOUND by caller)
 *   - 5xx → throws (caller retries once + maps to 502)
 */
export interface FetchCallRailRecordingUrlResult {
  readonly url: string;
}

export async function fetchCallRailRecordingUrl(
  apiToken: string,
  accountId: string,
  callId: string,
): Promise<FetchCallRailRecordingUrlResult> {
  const url = `${CALLRAIL_API_BASE}/a/${encodeURIComponent(accountId)}/calls/${encodeURIComponent(callId)}/recording.json`;
  const result = await safeFetchCallRail(url, apiToken, { method: "GET" });
  if (!result.success) {
    throw wrapTransportError(result.error, apiToken);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    const parsed = parseCallRailApiError(response.status, body, apiToken);
    throw new CallRailApiCallError(parsed);
  }
  const body = (await safeReadJson(response)) as
    | { url?: unknown }
    | null;
  if (!body || typeof body.url !== "string" || body.url.length === 0) {
    // Treat empty-body as "recording not found" — caller maps to 404.
    throw new CallRailApiCallError({
      type: "not_found",
      message: "CallRail recording.json returned empty body",
      status: 404,
      retryable: false,
    });
  }
  return { url: body.url };
}
